import React, { useState } from "react";
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
} from "@mui/material";
import { FilterList } from "@mui/icons-material";
import {
  CompanyFilter,
  DomainFilter,
  DepartmentFilter,
  JobRoleFilter,
  JobSettingFilter,
  CompanySizeFilter,
  SalaryRangeFilter,
  LocationFilter,
  DateFilter,
  CompanyTypeFilter,
  FundingTypeFilter,
  OperatingStatusFilter,
  YearFoundedRangeFilter,
} from "@/components/organisms/Filters";
import { SearchPanel } from "@/components/organisms";
import { Button, Accordion } from "@/components/atoms/index";
import { FilterJobsContextProps } from "@/providers/contexts/FilterJobsContext";

export interface FilterPanelProps {
  resetFilters: FilterJobsContextProps["resetFilters"];
  onSortByDate?: (isAscending: boolean) => void;
  sortAscending?: boolean | null;
  page: "search" | "directory";
  renderJobSetting?: boolean;
  renderDepartments?: boolean;
  renderJobRoles?: boolean;
  renderSalaryRange?: boolean;
  renderLocation?: boolean;
  renderCompanySize?: boolean;
  renderDomains?: boolean;
  renderDate?: boolean;
  renderFundingType?: boolean;
  renderCompanyType?: boolean;
  renderOperatingStatus?: boolean;
  renderYearFounded?: boolean;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  resetFilters,
  onSortByDate,
  sortAscending,
  page,
  renderJobSetting = true,
  renderDepartments = true,
  renderJobRoles = true,
  renderSalaryRange = true,
  renderLocation = true,
  renderCompanySize = true,
  renderDomains = true,
  renderDate = true,
  renderFundingType = false,
  renderCompanyType = false,
  renderOperatingStatus = false,
  renderYearFounded = false,
}) => {
  const [isExpanded, setExpanded] = useState(false);

  const toggleAccordion = () => {
    setExpanded(!isExpanded);
  };

  const toggleSortOrder = () => {
    const newOrder = !sortAscending;
    if (onSortByDate) {
      onSortByDate(newOrder);
    }
  };

  return (
    <Accordion expanded={isExpanded} componentName="filter-panel">
      <AccordionSummary
        component="div"
        aria-controls="more-filters-content"
        id="more-filters-header"
        sx={{
          "&.MuiButtonBase-root, & .MuiAccordionSummary-root, & .MuiAccordionSummary-content":
            {
              cursor: "default",
            },
          "&.Mui-focused, &:focus, &:hover, &.Mui-focusVisible": {
            backgroundColor: "transparent",
          },
        }}
      >
        <Box
          data-testid="non-expanded-filters"
          flexDirection="column"
          sx={{
            display: "flex",
            width: "100%",
          }}
        >
          <Box
            data-testid="non-expanded-first-row"
            flexDirection="row"
            columnGap={2}
            sx={{
              display: "flex",
              justifyContent: "right",
              alignItems: "center",
            }}
          >
            <SearchPanel page={page} />
            <Box
              flexDirection="row"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {onSortByDate && (
                <IconButton
                  data-testid="sort-by-date-button"
                  onClick={toggleSortOrder}
                  sx={{ color: "primary.main" }}
                  title={`For current page of results, sort by job post date ${
                    sortAscending ? "(oldest first)" : "(latest first)"
                  }`}
                >
                  <svg
                    style={{
                      transform: sortAscending
                        ? "rotate(0deg)"
                        : "rotate(180deg)",
                      transition: "transform 0.3s ease",
                    }}
                    xmlns="http://www.w3.org/2000/svg"
                    height="24"
                    viewBox="0 0 24 24"
                    width="24"
                    fill="currentColor"
                  >
                    <path d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.59 5.58L20 12l-8-8-8 8z" />
                  </svg>
                </IconButton>
              )}

              <IconButton
                onClick={toggleAccordion}
                title="Expand for more filters"
                sx={{
                  color: "primary.main",
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                <FilterList />
              </IconButton>
              <Button
                componentName={`filter-panel-reset`}
                variant="contained"
                color="secondary"
                sx={{ m: 2, float: "right" }}
                onClick={() => {
                  resetFilters();
                  setExpanded(false);
                }}
              >
                Reset
              </Button>
            </Box>
          </Box>
          <Box
            data-testid="non-expanded-second-row"
            flexDirection="row"
            columnGap={2}
            sx={{
              display: "flex",
              justifyContent: "right",
              alignItems: "center",
            }}
          >
            <CompanyFilter page={page} />
            {renderDomains && (
              <DomainFilter
                componentName={"filter-panel"}
                autocompleteSx={{
                  mt: 0,
                }}
                page={page}
              />
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <Box role="presentation" data-testid="expanded-filters">
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid container item xs={6} direction="column">
              {renderDepartments && <DepartmentFilter />}
              {renderJobRoles && <JobRoleFilter />}
              {renderLocation && <LocationFilter page={page} />}
              {renderSalaryRange && <SalaryRangeFilter />}
              {renderFundingType && <FundingTypeFilter />}
              {renderYearFounded && <YearFoundedRangeFilter />}
            </Grid>
            <Grid container item xs={6} direction="column">
              {renderJobSetting && <JobSettingFilter expanded={isExpanded} />}
              {renderDate && <DateFilter dateType="posted" />}
              {renderDate && <DateFilter dateType="updated" />}
              {renderCompanySize && <CompanySizeFilter page={page} />}
              {renderCompanyType && <CompanyTypeFilter />}
              {renderOperatingStatus && <OperatingStatusFilter />}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Box>
    </Accordion>
  );
};
